import {
  cardMargin,
  defaultCardMotion,
  type CardAnchor,
  type CardElement,
  type CardMotion,
  type CardPresetRef,
} from "./card.js";
import { bookendFonts } from "./font.js";
import type { Background } from "./scene.js";

/**
 * Ready-made cards.
 *
 * A card has a lot of knobs — position, alignment, font, size, weight, colour,
 * entry, exit, delay. Nobody opens a screen recorder wanting to make those nine
 * decisions; they want a card that looks right with their words in it. A preset
 * is a finished card rather than a theme laid over one: it owns every box, every
 * type decision, the background, and the motion on each element including the
 * delays that stagger them.
 *
 * The one thing it never owns is the writing. Applying a preset re-dresses what
 * is already there and hands nothing back to retype, which is what makes trying
 * six of them cheap instead of frightening.
 */

/** How one element is dressed, by its position in the card rather than its id. */
export interface CardPresetRole {
  anchor: CardAnchor;
  /** Fractions of the frame. `anchoredBox` decides x and y from the anchor. */
  width: number;
  height: number;
  align: "left" | "center" | "right";
  fontFamily: string;
  fontSize: number;
  bold: boolean;
  color: string;
  motion: CardMotion;
}

export interface CardPreset {
  id: string;
  label: string;
  background: Background;
  /**
   * One entry per element, in paint order. A card with more elements than the
   * preset names re-uses the last role — a fourth line dressed like the third
   * is a card; a fourth line left at 88px in the middle is a mess.
   */
  roles: CardPresetRole[];
}

const font = (id: string) => bookendFonts.find((entry) => entry.id === id)?.stack ?? bookendFonts[0].stack;

const move = (delay: number, type: CardMotion["entry"]["type"] = "rise", duration = 0.55): CardMotion => ({
  delay,
  entry: { type, duration },
  exit: { type: "none", duration: 0.5 },
});

export const cardPresets: readonly CardPreset[] = [
  {
    id: "plain",
    label: "Plain",
    background: { kind: "color", color: "#05070d" },
    roles: [
      {
        anchor: "middle-center",
        width: 0.8,
        height: 0.14,
        align: "center",
        fontFamily: font("inter"),
        fontSize: 88,
        bold: true,
        color: "#f8fafc",
        motion: move(0),
      },
      {
        anchor: "middle-center",
        width: 0.68,
        height: 0.07,
        align: "center",
        fontFamily: font("inter"),
        fontSize: 38,
        bold: false,
        color: "#cbd5e1",
        motion: move(0.16),
      },
    ],
  },
  {
    id: "rule",
    label: "Rule",
    background: { kind: "color", color: "#0b1020" },
    roles: [
      {
        anchor: "middle-left",
        width: 0.62,
        height: 0.14,
        align: "left",
        fontFamily: font("archivo"),
        fontSize: 92,
        bold: true,
        color: "#f8fafc",
        motion: move(0),
      },
      {
        anchor: "middle-left",
        width: 0.5,
        height: 0.07,
        align: "left",
        fontFamily: font("archivo"),
        fontSize: 34,
        bold: false,
        color: "#94a3b8",
        motion: move(0.18),
      },
    ],
  },
  {
    id: "centre",
    label: "Centre",
    background: { kind: "pattern", pattern: "glow", from: "#1e3a8a", to: "#0b1020" },
    roles: [
      {
        anchor: "middle-center",
        width: 0.76,
        height: 0.15,
        align: "center",
        fontFamily: font("playfair-display"),
        fontSize: 96,
        bold: true,
        color: "#f8fafc",
        motion: move(0, "fade", 0.7),
      },
      {
        anchor: "middle-center",
        width: 0.6,
        height: 0.07,
        align: "center",
        fontFamily: font("inter"),
        fontSize: 34,
        bold: false,
        color: "rgba(226,232,240,0.78)",
        motion: move(0.22, "fade", 0.7),
      },
    ],
  },
  {
    id: "left-rail",
    label: "Left rail",
    background: { kind: "pattern", pattern: "panel", from: "#111a2e", to: "#0b1020" },
    roles: [
      {
        anchor: "middle-left",
        width: 0.46,
        height: 0.16,
        align: "left",
        fontFamily: font("dm-sans"),
        fontSize: 78,
        bold: true,
        color: "#f8fafc",
        motion: move(0, "slide", 0.6),
      },
      {
        anchor: "middle-left",
        width: 0.4,
        height: 0.08,
        align: "left",
        fontFamily: font("dm-sans"),
        fontSize: 32,
        bold: false,
        color: "#93a4bd",
        motion: move(0.16, "slide", 0.6),
      },
    ],
  },
  {
    id: "lower-third",
    label: "Lower third",
    background: { kind: "pattern", pattern: "base", from: "#0b1020", to: "#132033" },
    roles: [
      {
        anchor: "bottom-left",
        width: 0.62,
        height: 0.12,
        align: "left",
        fontFamily: font("space-grotesk"),
        fontSize: 72,
        bold: true,
        color: "#f8fafc",
        motion: move(0),
      },
      {
        anchor: "bottom-left",
        width: 0.52,
        height: 0.06,
        align: "left",
        fontFamily: font("space-grotesk"),
        fontSize: 30,
        bold: false,
        color: "#9aa8bd",
        motion: move(0.14),
      },
    ],
  },
  {
    id: "corner",
    label: "Corner",
    background: { kind: "pattern", pattern: "corner", from: "#4c1d95", to: "#0b1020" },
    roles: [
      {
        anchor: "top-left",
        width: 0.6,
        height: 0.14,
        align: "left",
        fontFamily: font("fraunces"),
        fontSize: 84,
        bold: true,
        color: "#f8fafc",
        motion: move(0, "fade", 0.6),
      },
      {
        anchor: "top-left",
        width: 0.48,
        height: 0.07,
        align: "left",
        fontFamily: font("inter"),
        fontSize: 32,
        bold: false,
        color: "#c4b5fd",
        motion: move(0.2, "fade", 0.6),
      },
    ],
  },
  {
    id: "band",
    label: "Band",
    background: { kind: "pattern", pattern: "band", from: "#0b1020", to: "#152238" },
    roles: [
      {
        anchor: "middle-center",
        width: 0.76,
        height: 0.13,
        align: "center",
        fontFamily: font("bebas-neue"),
        fontSize: 110,
        bold: false,
        color: "#f8fafc",
        motion: move(0),
      },
      {
        anchor: "middle-center",
        width: 0.6,
        height: 0.06,
        align: "center",
        fontFamily: font("jetbrains-mono"),
        fontSize: 26,
        bold: false,
        color: "#7dd3fc",
        motion: move(0.18),
      },
    ],
  },
  {
    id: "stacked",
    label: "Stacked",
    background: { kind: "color", color: "#0b1020" },
    roles: [
      {
        anchor: "top-left",
        width: 0.7,
        height: 0.12,
        align: "left",
        fontFamily: font("libre-baskerville"),
        fontSize: 68,
        bold: true,
        color: "#f8fafc",
        motion: move(0),
      },
      {
        anchor: "middle-left",
        width: 0.62,
        height: 0.09,
        align: "left",
        fontFamily: font("inter"),
        fontSize: 36,
        bold: false,
        color: "#a8b4c8",
        motion: move(0.15),
      },
      {
        anchor: "bottom-left",
        width: 0.5,
        height: 0.06,
        align: "left",
        fontFamily: font("jetbrains-mono"),
        fontSize: 24,
        bold: false,
        color: "#64748b",
        motion: move(0.3),
      },
    ],
  },
] as const;

export function findCardPreset(id: string | undefined): CardPreset | undefined {
  return id ? cardPresets.find((preset) => preset.id === id) : undefined;
}

interface DressableCard {
  kind: "card";
  elements: CardElement[];
  background?: Background;
  preset?: CardPresetRef;
  [key: string]: unknown;
}

/**
 * Dresses a card in a preset, keeping every word on it.
 *
 * Text, images and shapes all take their box and motion from the role at their
 * position; only a text element takes the type treatment, because there is no
 * sensible font size for a rectangle. An unknown id returns the card untouched
 * rather than half-applying something.
 */
export function applyCardPreset<T extends DressableCard>(card: T, presetId: string): T {
  const preset = findCardPreset(presetId);
  if (!preset) return card;

  const elements = card.elements.map((element, index) => {
    const role = preset.roles[Math.min(index, preset.roles.length - 1)];
    // Only the anchor and the size are set here. Where the box actually lands
    // is `anchoredBoxes`' job, because two roles sharing a point have to stack
    // around it and no single element can know what else is pinned there.
    const dressed = {
      ...element,
      anchor: role.anchor,
      width: role.width,
      height: role.height,
      motion: { ...role.motion },
    };
    if (dressed.kind !== "text") return dressed;
    return {
      ...dressed,
      align: role.align,
      style: {
        ...dressed.style,
        fontFamily: role.fontFamily,
        fontSize: role.fontSize,
        bold: role.bold,
        color: role.color,
      },
    };
  });

  return { ...card, elements, background: preset.background, preset: { id: preset.id } };
}

/** The margin an anchored preset box keeps, re-exported so a preview can use it. */
export { cardMargin, defaultCardMotion };
